import styles from "./labor-costs-tab.module.css";
import LaborCostsTable from "@/components/tables/LaborCostsTable";
import EditIcon from "@/icons/EditIcon";
import PlusIcon from "@/icons/PlusIcon";
import InformationCircleIcon from "@/icons/InformationCircleIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";
import { getPluralizedWord } from "@/utils";
import {
  Spin,
  Empty,
  Button,
  Flex,
  Dropdown,
  Tooltip,
  Typography,
} from "antd";
const { Text } = Typography;

const toFloorNumber = (value, precision = 0) => {
  if (value === null || value === undefined || value === "") return null;

  const num = Number(String(value).replace(",", "."));
  if (!Number.isFinite(num)) return null;

  if (precision <= 0) {
    return Math.floor(num);
  }

  const factor = 10 ** precision;
  return Math.floor(num * factor) / factor;
};

// Страница трудозатрат
const LaborCostsTab = ({
  data = [],
  actual = 0,
  planned = null,
  childrenPlanned = null,
  childrenActual = 0,
  requireActualsOnAcceptance = false,
  doNotExceedChildren = false,
  taskHasChildren = false,
  pending,
  pagination,
  readOnly,
  canEditPlanned = true,
  canAddActual = true,
  handleDeleteLaborCosts,
  handleEditLaborCosts,
  handleAddLaborCosts,
  handleEditPlannedLaborCosts,
  onTableChange,
  isAdmins = false,
}) => {
  if (pending) {
    return (
      <div className="spin">
        <Spin size="large" />
      </div>
    );
  }

  const hasData = !!data.length;

  const plannedNumber = toFloorNumber(planned, 2);
  const actualNumber = actual ? toFloorNumber(actual, 2) : 0;

  const childrenPlannedNumber = toFloorNumber(childrenPlanned, 2);
  const childrenActualNumber = childrenActual ? toFloorNumber(childrenActual, 2) : 0;

  const childrenPlannedText =
    childrenPlannedNumber
      ? `${childrenPlannedNumber} ${getPluralizedWord(toFloorNumber(childrenPlanned), ["час", "часа", "часов"])}`
      : "часы не указаны";

  const childrenActualText =
    childrenActualNumber
      ? `${childrenActualNumber} ${getPluralizedWord(toFloorNumber(childrenActual) ?? 0, ["час", "часа", "часов"])}`
      : "0 часов";

  const plannedText =
    plannedNumber
      ? `${plannedNumber} ${getPluralizedWord(toFloorNumber(planned), ["час", "часа", "часов"])}`
      : "часы не указаны";

  const actualText =
    actualNumber
      ? `${actualNumber + childrenActualNumber} ${getPluralizedWord(toFloorNumber(actual) ?? 0, ["час", "часа", "часов"])}`
      : "0 часов";

  const isOverPlanned = plannedNumber && actualNumber && (actualNumber + childrenActualNumber > plannedNumber);
  const isOverChildrenPlanned = childrenPlannedNumber && childrenActualNumber && (childrenActualNumber > childrenPlannedNumber);

  const menu = {
    items: [
      { key: "actual", label: "Добавить фактические трудозатраты" },
      { key: "planned", label: "Изменить планируемые трудозатраты" },
    ],
    onClick: ({ key }) => {
      if (key === "actual") {
        handleAddLaborCosts();
      }
      if (key === "planned") {
        handleEditPlannedLaborCosts();
      }
    },
  };

  return (
    <>
      <Flex justify="space-between" align="start" gap={10}>
        <Flex vertical gap={10}>
          <Flex align="center" gap={8} className={styles.planned_labor_row}>
            <div>{`Планируемые трудозатраты: ${plannedText}`}</div>
            {doNotExceedChildren && !plannedNumber && (
              <Tooltip title='Плановые трудозатраты дочерних задач не могут превышать плановые трудозатраты данной задачи. Назначьте положительное значение плановых трудозатрат'>
                <span><ExclamationCircleIcon style={{ fontSize: 15, color: "#FF4D4F" }} /></span>
              </Tooltip>
            )}
            {requireActualsOnAcceptance && (
                <Tooltip title='Обязательно заполнение фактических трудозатрат перед переводом задачи в статус "Приемка"'>
                  <span><InformationCircleIcon style={{ fontSize: 15 }} /></span>
                </Tooltip>
              )}
            {canEditPlanned && (
              <Button
                size="small"
                icon={<EditIcon />}
                type="text"
                onClick={handleEditPlannedLaborCosts}
                className={styles.planned_labor_row__edit_btn}
              />
            )}
          </Flex>

          {actualNumber !== null && (
            <Flex align="center" gap={6} style={{ marginBottom: 12 }}>
              <Flex gap={1}>
                <span>Фактические трудозатраты:&nbsp;</span>
                <Text type={isOverPlanned ? "danger" : undefined}>
                  {`${actualText}`}
                </Text>
              </Flex>

              {requireActualsOnAcceptance && (
                <Tooltip title='Обязательно заполнение фактических трудозатрат перед переводом задачи в статус "Приемка"'>
                  <span><InformationCircleIcon style={{ fontSize: 15 }} /></span>
                </Tooltip>
              )}
            </Flex>
          )}

          {taskHasChildren && (
            <>
              <Flex align="center" gap={8} className={styles.planned_labor_row}>
                <div>{`Планируемые трудозатраты дочерних задач: ${childrenPlannedText}`}</div>
                {doNotExceedChildren && (
                  <Tooltip title='Плановые трудозатраты дочерних задач не могут превышать плановые трудозатраты родительской'>
                    <span><InformationCircleIcon style={{ fontSize: 15 }} /></span>
                  </Tooltip>
                )}
              </Flex>

              {childrenActualNumber !== null && (
                <Flex gap={1} style={{ marginBottom: 12 }}>
                  <span>Фактические трудозатраты дочерних задач:&nbsp;</span>
                  <Text type={isOverChildrenPlanned ? "danger" : undefined}>
                    {`${childrenActualText}`}
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Flex>

        {!readOnly && canEditPlanned && canAddActual && (
          <Dropdown
            menu={menu}
            trigger={["click"]}
            placement="bottomLeft"
            getPopupContainer={() => document.body}
          >
            <Button type="primary" icon={<PlusIcon />} />
          </Dropdown>
        )}

        {!readOnly && !canEditPlanned && canAddActual && (
          <Button
            type="primary"
            icon={<PlusIcon />}
            onClick={handleAddLaborCosts}
          />
        )}
      </Flex>

      {!readOnly && canAddActual && (
        <Button
          icon={<PlusIcon />}
          style={{ alignSelf: "flex-start", marginBottom: 12 }}
          onClick={handleAddLaborCosts}
        >
          Внести трудозатраты
        </Button>
      )}

      {hasData ? (
        <LaborCostsTable
          data={data}
          loading={pending}
          pagination={pagination}
          readOnly={readOnly}
          onEditItem={handleEditLaborCosts}
          onDeleteItem={handleDeleteLaborCosts}
          isAdmins={isAdmins}
          onChange={onTableChange}
          canEditActual={canAddActual}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </>
  );
};

export default LaborCostsTab;
